/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './growth-legend.reducer';

export const GrowthLegend = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const growthLegendList = useAppSelector(state => state.growthLegend.entities);
  const loading = useAppSelector(state => state.growthLegend.loading);
  const totalItems = useAppSelector(state => state.growthLegend.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="growth-legend-heading" data-cy="GrowthLegendHeading">
        <Translate contentKey="tinyMedicRestApp.growthLegend.home.title">Growth Legends</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.growthLegend.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/growth-legend/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.growthLegend.home.createLabel">Create new Growth Legend</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.growthLegend.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {growthLegendList && growthLegendList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('reportType')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.reportType">Report Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('reportType')} />
                </th>
                <th className="hand" onClick={sort('gender')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.gender">Gender</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('gender')} />
                </th>
                <th className="hand" onClick={sort('chartType')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.chartType">Chart Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('chartType')} />
                </th>
                <th className="hand" onClick={sort('ageFrom')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.ageFrom">Age From</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ageFrom')} />
                </th>
                <th className="hand" onClick={sort('ageTo')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.ageTo">Age To</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ageTo')} />
                </th>
                <th className="hand" onClick={sort('ageUnit')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.ageUnit">Age Unit</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ageUnit')} />
                </th>
                <th className="hand" onClick={sort('lambda')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.lambda">Lambda</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lambda')} />
                </th>
                <th className="hand" onClick={sort('mean')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.mean">Mean</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('mean')} />
                </th>
                <th className="hand" onClick={sort('sigma')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.sigma">Sigma</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sigma')} />
                </th>
                <th className="hand" onClick={sort('delta')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.delta">Delta</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('delta')} />
                </th>
                <th className="hand" onClick={sort('p01')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p01">P 01</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p01')} />
                </th>
                <th className="hand" onClick={sort('p1')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p1">P 1</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p1')} />
                </th>
                <th className="hand" onClick={sort('p3')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p3">P 3</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p3')} />
                </th>
                <th className="hand" onClick={sort('p5')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p5">P 5</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p5')} />
                </th>
                <th className="hand" onClick={sort('p10')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p10">P 10</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p10')} />
                </th>
                <th className="hand" onClick={sort('p15')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p15">P 15</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p15')} />
                </th>
                <th className="hand" onClick={sort('p25')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p25">P 25</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p25')} />
                </th>
                <th className="hand" onClick={sort('p50')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p50">P 50</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p50')} />
                </th>
                <th className="hand" onClick={sort('p75')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p75">P 75</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p75')} />
                </th>
                <th className="hand" onClick={sort('p85')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p85">P 85</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p85')} />
                </th>
                <th className="hand" onClick={sort('p90')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p90">P 90</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p90')} />
                </th>
                <th className="hand" onClick={sort('p95')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p95">P 95</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p95')} />
                </th>
                <th className="hand" onClick={sort('p97')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p97">P 97</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p97')} />
                </th>
                <th className="hand" onClick={sort('p99')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p99">P 99</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p99')} />
                </th>
                <th className="hand" onClick={sort('p999')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.p999">P 999</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('p999')} />
                </th>
                <th className="hand" onClick={sort('SD4neg')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.SD4neg">SD 4 Neg</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('SD4neg')} />
                </th>
                <th className="hand" onClick={sort('SD3neg')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.SD3neg">SD 3 Neg</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('SD3neg')} />
                </th>
                <th className="hand" onClick={sort('SD2neg')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.SD2neg">SD 2 Neg</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('SD2neg')} />
                </th>
                <th className="hand" onClick={sort('SD1neg')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.SD1neg">SD 1 Neg</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('SD1neg')} />
                </th>
                <th className="hand" onClick={sort('SD0')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.SD0">SD 0</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('SD0')} />
                </th>
                <th className="hand" onClick={sort('SD1')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.SD1">SD 1</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('SD1')} />
                </th>
                <th className="hand" onClick={sort('SD2')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.SD2">SD 2</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('SD2')} />
                </th>
                <th className="hand" onClick={sort('SD3')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.SD3">SD 3</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('SD3')} />
                </th>
                <th className="hand" onClick={sort('SD4')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.SD4">SD 4</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('SD4')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('publishedYear')}>
                  <Translate contentKey="tinyMedicRestApp.growthLegend.publishedYear">Published Year</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('publishedYear')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {growthLegendList.map((growthLegend, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/growth-legend/${growthLegend.id}`} color="link" size="sm">
                      {growthLegend.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.ReportType.${growthLegend.reportType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.Gender.${growthLegend.gender}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.GrowthChartType.${growthLegend.chartType}`} />
                  </td>
                  <td>{growthLegend.ageFrom}</td>
                  <td>{growthLegend.ageTo}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.AgeUnit.${growthLegend.ageUnit}`} />
                  </td>
                  <td>{growthLegend.lambda}</td>
                  <td>{growthLegend.mean}</td>
                  <td>{growthLegend.sigma}</td>
                  <td>{growthLegend.delta}</td>
                  <td>{growthLegend.p01}</td>
                  <td>{growthLegend.p1}</td>
                  <td>{growthLegend.p3}</td>
                  <td>{growthLegend.p5}</td>
                  <td>{growthLegend.p10}</td>
                  <td>{growthLegend.p15}</td>
                  <td>{growthLegend.p25}</td>
                  <td>{growthLegend.p50}</td>
                  <td>{growthLegend.p75}</td>
                  <td>{growthLegend.p85}</td>
                  <td>{growthLegend.p90}</td>
                  <td>{growthLegend.p95}</td>
                  <td>{growthLegend.p97}</td>
                  <td>{growthLegend.p99}</td>
                  <td>{growthLegend.p999}</td>
                  <td>{growthLegend.SD4neg}</td>
                  <td>{growthLegend.SD3neg}</td>
                  <td>{growthLegend.SD2neg}</td>
                  <td>{growthLegend.SD1neg}</td>
                  <td>{growthLegend.SD0}</td>
                  <td>{growthLegend.SD1}</td>
                  <td>{growthLegend.SD2}</td>
                  <td>{growthLegend.SD3}</td>
                  <td>{growthLegend.SD4}</td>
                  <td>{growthLegend.active ? 'true' : 'false'}</td>
                  <td>{growthLegend.timestampentry}</td>
                  <td>{growthLegend.microSec}</td>
                  <td>{growthLegend.lastupdated}</td>
                  <td>{growthLegend.cursor}</td>
                  <td>{growthLegend.publishedYear}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/growth-legend/${growthLegend.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/growth-legend/${growthLegend.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/growth-legend/${growthLegend.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.growthLegend.home.notFound">No Growth Legends found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={growthLegendList && growthLegendList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default GrowthLegend;
